import React from 'react';
import SectionIntro from '../primitives/SectionIntro';
import HelperNote from '../primitives/HelperNote';
import ApplicantHeaderChip from '../primitives/ApplicantHeaderChip';
import PillGroup from '../primitives/PillGroup';
import DateTriplet from '../primitives/DateTriplet';
import TextField from '../primitives/TextField';
import CurrencyField from '../primitives/CurrencyField';
import AddressAutocomplete from '../primitives/AddressAutocomplete';
import RepeaterCard from '../primitives/RepeaterCard';
import CategoryCard, { ICONS } from '../primitives/CategoryCard';
import GapAlert from '../primitives/GapAlert';
import { coverageAnalysis, formatCoverage } from '../dateUtils';

const STATUS_OPTIONS = ['Full Time', 'Part Time', 'Casual', 'Contractor', 'Self-employed', 'Not employed', 'Student', 'Retired'];

class StepEmployment extends React.Component {
  applicant = () => this.props.applicants.find((a) => a.id === this.props.applicantId) || this.props.applicants[0];

  patchCurrent = (field, value) => {
    const applicant = this.applicant();
    const employment = applicant.employment || { current: {}, previous: [] };
    this.props.onUpdateApplicant(applicant.id, {
      employment: { ...employment, current: { ...(employment.current || {}), [field]: value } },
    });
  };

  patchPrevious = (index, field, value) => {
    const applicant = this.applicant();
    const employment = applicant.employment || { current: {}, previous: [] };
    const previous = [...(employment.previous || [])];
    previous[index] = { ...(previous[index] || {}), [field]: value };
    this.props.onUpdateApplicant(applicant.id, {
      employment: { ...employment, previous },
    });
  };

  addPrevious = () => {
    const applicant = this.applicant();
    const employment = applicant.employment || { current: {}, previous: [] };
    const previous = [...(employment.previous || [])];
    previous.push({
      position: '',
      employer_name: '',
      employer_phone: '',
      employer_address: '',
      gross_income: '',
      employment_status: '',
      start_date: { day: '', month: '', year: '' },
      end_date: { day: '', month: '', year: '' },
    });
    this.props.onUpdateApplicant(applicant.id, {
      employment: { ...employment, previous },
    });
  };

  removePrevious = (index) => {
    const applicant = this.applicant();
    const employment = applicant.employment || { current: {}, previous: [] };
    const previous = [...(employment.previous || [])];
    previous.splice(index, 1);
    this.props.onUpdateApplicant(applicant.id, {
      employment: { ...employment, previous },
    });
  };

  computeCoverage = (current, previous) => {
    const entries = [];
    if (current && current.start_date) entries.push({ startDate: current.start_date, endDate: null });
    (previous || []).forEach((p) => {
      if (p.start_date) entries.push({ startDate: p.start_date, endDate: p.end_date });
    });
    return coverageAnalysis(entries);
  };

  renderEmploymentFields = (slot, index, job) => {
    const prefix = slot === 'current' ? 'employment.current' : `employment.previous.${index}`;
    const errs = this.props.errors || {};
    return (
      <>
        <TextField
          label="Position / job title"
          required
          value={job.position}
          onChange={(v) => slot === 'current' ? this.patchCurrent('position', v) : this.patchPrevious(index, 'position', v)}
          fieldId={`${prefix}.position`}
          error={errs[`${prefix}.position`]}
        />
        <TextField
          label="Employer name"
          required
          value={job.employer_name}
          onChange={(v) => slot === 'current' ? this.patchCurrent('employer_name', v) : this.patchPrevious(index, 'employer_name', v)}
          fieldId={`${prefix}.employer_name`}
          error={errs[`${prefix}.employer_name`]}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            label="Employer phone"
            required
            inputMode="tel"
            value={job.employer_phone}
            onChange={(v) => slot === 'current' ? this.patchCurrent('employer_phone', v) : this.patchPrevious(index, 'employer_phone', v)}
            fieldId={`${prefix}.employer_phone`}
            error={errs[`${prefix}.employer_phone`]}
          />
          <CurrencyField
            label="Gross income (annual, pre-tax)"
            required
            value={job.gross_income}
            onChange={(v) => slot === 'current' ? this.patchCurrent('gross_income', v) : this.patchPrevious(index, 'gross_income', v)}
            suffix="/year"
            fieldId={`${prefix}.gross_income`}
            error={errs[`${prefix}.gross_income`]}
          />
        </div>
        <AddressAutocomplete
          label="Employer address"
          required
          // No country field on a job, and employers can be overseas, so
          // search worldwide rather than defaulting to Australia only.
          unrestricted
          value={job.employer_address}
          onChange={(v) => slot === 'current' ? this.patchCurrent('employer_address', v) : this.patchPrevious(index, 'employer_address', v)}
          onSelect={(sel) => slot === 'current' ? this.patchCurrent('employer_address', sel.formatted) : this.patchPrevious(index, 'employer_address', sel.formatted)}
          fieldId={`${prefix}.employer_address`}
          error={errs[`${prefix}.employer_address`]}
        />
        <div>
          <label className="block text-sm font-medium text-[#1F2937] mb-2">
            Employment status <span className="text-[#DC2626]">*</span>
          </label>
          <PillGroup
            columns={4}
            options={STATUS_OPTIONS}
            value={job.employment_status}
            onChange={(v) => slot === 'current' ? this.patchCurrent('employment_status', v) : this.patchPrevious(index, 'employment_status', v)}
            fieldId={`${prefix}.employment_status`}
            error={errs[`${prefix}.employment_status`]}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <DateTriplet
            label={slot === 'current' ? 'Start date' : 'Started'}
            required
            value={job.start_date}
            onChange={(v) => slot === 'current' ? this.patchCurrent('start_date', v) : this.patchPrevious(index, 'start_date', v)}
            fieldId={`${prefix}.start_date`}
            error={errs[`${prefix}.start_date`]}
          />
          {slot === 'previous' ? (
            <DateTriplet
              label="Ended"
              required
              value={job.end_date}
              onChange={(v) => this.patchPrevious(index, 'end_date', v)}
              fieldId={`${prefix}.end_date`}
              error={errs[`${prefix}.end_date`]}
            />
          ) : null}
        </div>
      </>
    );
  };

  render() {
    const { applicants, applicantId, onUpdateApplicant } = this.props;
    const idx = applicants.findIndex((a) => a.id === applicantId);
    const applicantIndex = idx === -1 ? 0 : idx;
    const applicant = applicants[applicantIndex];
    const employment = applicant.employment || { current: {}, previous: [] };
    const current = employment.current || {};
    const previous = employment.previous || [];
    const analysis = this.computeCoverage(current, previous);
    const months = analysis.months;
    const hasGap = analysis.hasGap;
    const meets = months >= 36 && !hasGap;

    return (
      <div className="space-y-5">
        {/* Intro */}
        <div className="rounded-2xl bg-white shadow-sm border border-[#E5E7EB] p-5 sm:p-8">
          <SectionIntro
            eyebrow="About you - Employment"
            title="3 years of employment history"
            description="Give us details for your main source of income, then add any previous jobs until you've covered at least 3 years. If you're between jobs or studying, choose the matching status."
          />
          <ApplicantHeaderChip
            applicant={applicant}
            applicantIndex={applicantIndex}
            totalApplicants={applicants.length}
          />
          <HelperNote tone={meets ? undefined : 'warning'}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <span>
                <strong>{meets ? 'Great, 3 years covered.' : 'Keep going.'} </strong>
                <strong>{formatCoverage(months)}</strong> of employment history so far. We need at least 3 years.
              </span>
              <span
                className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold"
                style={{
                  backgroundColor: meets ? '#DCFCE7' : '#FEF3C7',
                  color: meets ? '#166534' : '#92400E',
                }}
              >
                {formatCoverage(months)} / 3y
              </span>
            </div>
          </HelperNote>

          {hasGap ? (
            <div className="mt-4">
              <GapAlert kind="employment" gaps={analysis.gaps} />
            </div>
          ) : null}
        </div>

        {/* Current employment */}
        <CategoryCard
          icon={ICONS.briefcase}
          title="Current employment"
          description="Your main source of income right now."
        >
          <RepeaterCard title="Main source of income" removable={false} badge="Current">
            {this.renderEmploymentFields('current', null, current)}
          </RepeaterCard>
        </CategoryCard>

        {/* Previous employment */}
        <CategoryCard
          icon={ICONS.history}
          title="Previous employment"
          description="Roles before your current one to get you to 3 years of history."
          count={previous.length}
        >
          {previous.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-5 text-center">
              <p className="text-sm text-[#6B7280]">
                No previous jobs yet. Add one if your current role has been yours for less than 3 years.
              </p>
            </div>
          ) : null}

          {previous.map((job, idx) => (
            <RepeaterCard
              key={idx}
              title={`Previous employment ${idx + 1}`}
              onRemove={() => this.removePrevious(idx)}
            >
              {this.renderEmploymentFields('previous', idx, job)}
            </RepeaterCard>
          ))}

          <button
            type="button"
            onClick={this.addPrevious}
            className="w-full rounded-xl border-2 border-dashed py-3 text-sm font-semibold transition-colors"
            style={{ borderColor: '#7C5CFC', color: '#5B3FE0', backgroundColor: '#FFFFFF' }}
          >
            + Add previous employment
          </button>

          {hasGap ? (
            <div className="mt-4">
              <GapAlert kind="employment" gaps={analysis.gaps} />
            </div>
          ) : null}
        </CategoryCard>

        {/* Other income */}
        <CategoryCard
          icon={ICONS.money}
          title="Other income"
          description="Anything else you earn outside the role above (this applicant only)."
        >
          <label className="block text-sm font-medium text-[#1F2937] mb-2">
            Any other income to declare?
          </label>
          <textarea
            rows={4}
            value={applicant.other_income_details || ''}
            onChange={(e) => onUpdateApplicant(applicant.id, { other_income_details: e.target.value })}
            placeholder="e.g. government allowances, dividends, child support, regular bonuses or commissions..."
            className="dui-textarea w-full rounded-lg border bg-white p-3 text-sm text-[#1F2937] focus:border-[#7C5CFC] focus:outline-none focus:ring-2 focus:ring-[#7C5CFC]"
            style={{ borderColor: '#D1D5DB' }}
          />
          <p className="mt-2 text-xs text-[#6B7280]">
            Don't include rental income here - we'll capture that with the property details later.
          </p>
        </CategoryCard>
      </div>
    );
  }
}

export default StepEmployment;
